/**
 * Arrival rules that survive the form rebuild.
 *
 * The storage rule and the shelf-life arithmetic. Per-bag weighing lives in `bags.ts`: the
 * handheld posts one bag at a time and the line's Actual pair is derived from those rows, which is
 * what the ArrivalUnloading app has always done — an earlier reading of seedtrac as "a form, not a
 * weighing workflow" was wrong, and `document_line_bags` is the table that answers it.
 */
import { Decimal, toDecimal, ZERO, type DecimalInput } from "./decimal";

export interface SlocBin {
  locationId: number;
  qty: DecimalInput;
  bags: number;
}
/** The captured Actual pair for a line: net quantity and bag count. */
export interface SlocLine {
  netQty: DecimalInput;
  bags: number;
}

/**
 * Rule A2: what an arrival puts away must equal what it received, exactly — same quantity, same bag
 * count, no bin listed twice. Zero tolerance is deliberate: a split that does not reconcile is a
 * counting mistake, and letting it through leaves the ledger and the shelf disagreeing forever.
 *
 * Named for SLOC, seedtrac's own word: "put away" appears 0 times in the 619-page manual, "SLOC" on
 * 57 of them.
 */
export function slocViolations(
  line: SlocLine,
  split: readonly SlocBin[]
): string[] {
  const net = toDecimal(line.netQty);
  const out: string[] = [];
  // seedtrac labels the first SLOC "(1st SLOC Mandatory)" and refuses a lot with none. Without
  // this the quantity check below still catches it, but says "0.000 must equal 297.800", which
  // tells an operator who simply has not chosen a bin yet nothing about what to do.
  if (split.length === 0 && (net.gt(0) || line.bags > 0))
    out.push("at least one storage location is required");
  const seen = new Set<number>();
  for (const s of split) {
    if (seen.has(s.locationId)) out.push(`bin ${s.locationId} listed twice`);
    seen.add(s.locationId);
    if (toDecimal(s.qty).lte(0))
      out.push(`bin ${s.locationId}: quantity must be greater than zero`);
  }
  const splitQty = split.reduce((a, s) => a.plus(toDecimal(s.qty)), ZERO);
  const splitBags = split.reduce((a, s) => a + s.bags, 0);
  if (!splitQty.eq(net))
    out.push(
      `storage quantity ${splitQty.toFixed(3)} must equal captured net ${net.toFixed(3)}`
    );
  if (splitBags !== line.bags)
    out.push(
      `storage bag count ${splitBags} must equal captured bags ${line.bags}`
    );
  return out;
}

/**
 * Life Expectancy (LE) = Harvest Date + LE Duration months, shown read-only on the post item form
 * beside "From Harvest Date" (p66).
 *
 * The stored value is `document_lines.life_expectancy_date`, a GENERATED column, so that no write
 * path can leave it stale. This helper exists ONLY for the live preview the operator sees before the
 * row is saved, and it must agree with Postgres exactly — `apps/api/test/arrival.test.ts` pins the
 * two against each other over a table of dates.
 *
 * Postgres `date + make_interval(months => n)` clamps an overflowing day to the end of the target
 * month (2026-01-31 + 1 month = 2026-02-28), which is NOT what `Date.setMonth` does — it would roll
 * into March. Clamping explicitly is the whole reason this is not a one-liner.
 */
export function lifeExpectancy(
  harvestDate: string | null | undefined,
  months: number | null | undefined
): string | null {
  if (!harvestDate || months === null || months === undefined) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(harvestDate.trim());
  if (!m || !Number.isFinite(months)) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const total = y * 12 + (mo - 1) + Math.trunc(months);
  const ty = Math.floor(total / 12);
  const tm = total - ty * 12; // 0-based
  // Day 0 of the NEXT month is the last day of this one.
  const lastDay = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  const p = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${p(ty, 4)}-${p(tm + 1)}-${p(day)}`;
}

/**
 * A weighbridge reading, or null when there is nothing to read.
 *
 * Deliberately NOT `toDecimal`, which treats blank and garbage alike as zero (it mirrors vnr-prod's
 * `to_decimal` for quantities, where that is right). Here it would be wrong twice over: a blank
 * Empty Truck Weight would make Net read as the whole loaded weight, and a half-typed "12." would
 * flicker a number the operator never entered. Absent has to stay absent.
 */
function reading(value: DecimalInput): Decimal | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  try {
    const d = new Decimal(value);
    return d.isFinite() ? d : null;
  } catch {
    return null;
  }
}

/**
 * Net Weight = Loaded Truck Weight − Empty Truck Weight, to the schema's 3 decimals.
 *
 * Null until BOTH readings are present, because a half-filled weighbridge has no net. The value is
 * shown read-only on the header and recomputed server-side on every write, so this helper exists to
 * keep the operator's live preview and the stored `documents.weighbridge.netKg` in step — the same
 * job, and the same reason, as `lifeExpectancy` above.
 *
 * A negative result is returned as-is, never clamped: see `weighbridgeViolations`.
 */
export function weighbridgeNet(
  loadedKg: DecimalInput,
  emptyKg: DecimalInput
): string | null {
  const loaded = reading(loadedKg);
  const empty = reading(emptyKg);
  if (loaded === null || empty === null) return null;
  return loaded.minus(empty).toFixed(3);
}

/**
 * The truck weights turned into each lot's recovered quantity (Cob Arrival).
 *
 * BOTH readings are typed at the weighbridge: the truck is weighed loaded, then weighed again
 * empty, and the difference is what actually arrived. The Seed Dispatch Note's own lot-by-lot
 * total — the PDN qty — is a separate figure entirely, and recovery is what arrived measured
 * against what the note declared:
 *
 *   pdnQty = Σ SDN qty                                    (11310)
 *   net    = loaded − empty      (12000 − 1200)           (10800)  — the actual total qty
 *   pct    = net / pdnQty × 100, half-up to 2 dp          (95.49)  — shown only, never applied
 *   share  = lot qty × net / pdnQty, exact                (3665 → 3499.7347…)
 *   actual = whole kg by largest remainder                (3665 → 3500)
 *
 * Largest remainder (the user's rule, 2026-10-13): every lot first takes the floor of its exact
 * share, then the kilograms still missing go one each to the lots with the biggest fractional
 * remainders, earlier lots first on a tie. So the lots ALWAYS add up to net exactly and no lot is
 * more than 1 kg from its exact share. A net with grams is allocated as its half-up whole kg.
 *
 * Nulls until BOTH readings are present and the note has a positive total. A lot with no quantity
 * yet, or a negative or unreadable one, has no actual and takes no share; a zero lot gets 0. Nothing
 * is clamped: an empty weight heavier than the loaded one gives a negative percentage, and a truck
 * heavier than its note gives one over 100 — `weighbridgeViolations` is what speaks about the
 * first, and the second is a real thing a desk may see.
 */
export function recoveryFigures(
  declaredQtys: readonly DecimalInput[],
  loadedKg: DecimalInput,
  emptyKg: DecimalInput
): {
  netKg: string | null;
  pct: string | null;
  actual: Array<string | null>;
} {
  // The same helper the non-recovery forms use, so one definition of Net serves every form.
  const netKg = weighbridgeNet(loadedKg, emptyKg);
  const qtys = declaredQtys.map((q) => {
    const d = reading(q);
    return d !== null && d.gte(0) ? d : null;
  });
  const pdnQty = qtys.reduce<Decimal>((a, q) => a.plus(q ?? ZERO), ZERO);
  if (netKg === null || !pdnQty.gt(0))
    return { netKg, pct: null, actual: declaredQtys.map(() => null) };
  const net = toDecimal(netKg);
  const target = net.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
  const shares = qtys.map((q) =>
    q === null ? null : q.times(net).div(pdnQty)
  );
  const kg = shares.map((sh) => (sh === null ? null : sh.floor()));
  const allocated = kg.reduce<Decimal>((a, k) => a.plus(k ?? ZERO), ZERO);
  // Whole kg still to hand out: below the lot count, since each floor gave up less than 1 kg.
  let left = target.minus(allocated).toNumber();
  const byRemainder = shares
    .flatMap((sh, i) =>
      sh === null ? [] : [{ i, rem: sh.minus(kg[i] as Decimal) }]
    )
    .sort((a, b) => b.rem.comparedTo(a.rem) || a.i - b.i);
  for (const { i } of byRemainder) {
    if (left <= 0) break;
    kg[i] = (kg[i] as Decimal).plus(1);
    left--;
  }
  return {
    netKg,
    pct: net
      .div(pdnQty)
      .times(100)
      .toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      .toFixed(2),
    actual: kg.map((k) => (k === null ? null : k.toFixed(3))),
  };
}

/**
 * What is wrong with a weighbridge pair, as operator-facing text.
 *
 * An UNWEIGHED VEHICLE IS LEGAL — both readings blank is silence, not an error. The reading is
 * documentation: rule A3 removed header-level weighbridge apportionment, so nothing downstream
 * divides by it and an arrival with no ticket must still submit.
 *
 * An empty truck heavier than the loaded one names BOTH figures rather than reporting "negative
 * net", because the operator's next move is to find which of the two was typed into the wrong box.
 */
export function weighbridgeViolations(
  loadedKg: DecimalInput,
  emptyKg: DecimalInput
): string[] {
  const loaded = reading(loadedKg);
  const empty = reading(emptyKg);
  if (loaded === null && empty === null) return [];
  const out: string[] = [];
  if (loaded !== null && loaded.lte(0))
    out.push("Loaded Truck Weight must be greater than zero.");
  if (empty !== null && empty.lt(0))
    out.push("Empty Truck Weight cannot be negative.");
  if (loaded !== null && empty !== null && empty.gt(loaded))
    out.push(
      `Empty Truck Weight ${empty.toFixed(3)} is heavier than Loaded Truck Weight ${loaded.toFixed(3)}.`
    );
  return out;
}
